import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState, type FC } from "react";
import { convertToLettersOnly } from "../../../utils/convert-to-letters-only";
import { uuidv4, type UUID } from "../../../utils/uuid";
import { SegmentSelect } from "../../message/editor/plugins/dynamic-content-plugin/segment-select";
import { useSegmentsWithFavourites } from "../../segment/server/use-segments";

export type DisplayConditionData = {
  type: string;
  label: string;
  description: string;
  before: string;
  after: string;
  id: UUID;
};

type DisplayConditionDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: DisplayConditionData) => void;
  selectedId?: UUID;
  nestedBlocked?: boolean;
};

export const DisplayConditionDialog: FC<DisplayConditionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  selectedId,
  nestedBlocked = false,
}) => {
  const [segmentId, setSegmentId] = useState<UUID | null>(selectedId ?? null);

  // Load segments logic
  const segmentLoader = useSegmentsWithFavourites({
    include_deleted: false,
    is_recipient_type: false,
  });

  const handleSegmentChange = useCallback((id: UUID | null) => {
    setSegmentId(id);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!segmentId) return;

    const selectedSegment = segmentLoader.data?.find((s) => s.id === segmentId);
    const segmentName = selectedSegment?.name || "Unknown Segment";

    onConfirm({
      type: "Segment",
      label: segmentName,
      description: `Only people in the segment: ${segmentName}`,
      before: `{{#if ${convertToLettersOnly(segmentId)}}}`,
      after: "{{/if}}",
      id: segmentId,
    });

    setSegmentId(null);
  }, [segmentId, segmentLoader.data, onConfirm]);

  const handleClose = useCallback(() => {
    onClose();
    setSegmentId(null);
  }, [onClose]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Select Display Condition Segment</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose a segment. This block will only be visible to users in this segment.
          </Typography>
          {nestedBlocked && (
            <Alert severity="warning">
              Display Conditions cannot be added inside another conditional block.
            </Alert>
          )}
          <SegmentSelect
            segmentLoader={segmentLoader}
            onChange={handleSegmentChange}
            segmentInfo={{
              selected: segmentId ?? selectedId ?? null,
              createNew: useMemo(() => uuidv4(), []), // Memoize to prevent regeneration
            }}
            disabled={nestedBlocked}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} data-analytics-id="cancel-display-condition">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          disabled={nestedBlocked || !segmentId}
          data-analytics-id="save-display-condition"
        >
          Save Condition
        </Button>
      </DialogActions>
    </Dialog>
  );
};
